/* eslint-disable no-return-assign */
/* eslint-disable no-lonely-if */
import * as Yup from 'yup';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// @mui
import { Stack, IconButton, InputAdornment, Box, Typography, Divider, Button, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';

// auth action
// Note: Ensure your actual actions are imported correctly here
import { loginUser, loginWithGoogle, sendOTP, loginWithOTP } from '../../../store/actions/auth';

// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// --- Styled Components ---

const AppleTextField = styled(RHFTextField)({
  '& .MuiOutlinedInput-root': {
    fontFamily: '"SF Pro Text", -apple-system, sans-serif',
    borderRadius: 12,
    background: '#f5f5f7',
    border: '1.5px solid #d2d2d7',
    '& fieldset': { border: '1.5px solid transparent' },
    '&.Mui-focused': {
      background: '#ffffff',
      boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.12)',
      '& fieldset': { borderColor: '#0071e3' },
    },
  },
});

const SubmitButton = styled(LoadingButton)(({ theme, disabled }) => ({
  borderRadius: 12,
  height: 50,
  textTransform: 'none',
  color: 'white',
  fontWeight: 590,
  background: disabled ? '#bcbcc5' : 'linear-gradient(180deg, #1c1c1e 0%, #000000 100%)',
  '&:hover': { background: disabled ? '#bcbcc5' : '#2c2c2e' },
  boxShadow: 'none',
  '&.Mui-disabled': {
    color: 'white',
    background: '#bcbcc5',
  },
}));

const SocialButton = styled(Button)({
  borderRadius: 12,
  height: 50,
  textTransform: 'none',
  fontWeight: 500,
  border: '1.5px solid #d2d2d7',
  color: '#1d1d1f',
  backgroundColor: '#fff',
  '&:hover': { backgroundColor: '#f5f5f7', borderColor: '#d2d2d7' },
});

// --- Custom Components ---

const OTPInput = ({ name }) => {
  const { control } = useFormContext();
  const inputRefs = useRef([]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const otpValues = (value || '').split('').concat(Array(6).fill('')).slice(0, 6);

        const handleChange = (e, index) => {
          const val = e.target.value;
          if (/[^0-9]/.test(val)) return;

          const newOtp = [...otpValues];
          newOtp[index] = val.substring(val.length - 1);

          onChange(newOtp.join(''));

          if (val && index < 5) {
            inputRefs.current[index + 1]?.focus();
          }
        };

        const handleKeyDown = (e, index) => {
          if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
          }
        };

        const handlePaste = (e) => {
          e.preventDefault();
          const pasteData = e.clipboardData
            .getData('text')
            .replace(/[^0-9]/g, '')
            .substring(0, 6);
          if (pasteData) {
            onChange(pasteData);
            const focusIndex = Math.min(pasteData.length, 5);
            if (inputRefs.current[focusIndex]) {
              inputRefs.current[focusIndex].focus();
            } else if (inputRefs.current[5]) {
              inputRefs.current[5].focus();
            }
          }
        };

        return (
          <Box>
            <Typography sx={{ mb: 1, color: '#6e6e73', fontWeight: 500, fontSize: '0.875rem' }}>OTP</Typography>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              {otpValues.map((digit, index) => (
                <Box
                  key={index}
                  component="input"
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  sx={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '6px',
                    border: '1.5px solid #d2d2d7',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontFamily: '"SF Pro Text", -apple-system, sans-serif',
                    background: '#ffffff',
                    outline: 'none',
                    '&:focus': {
                      borderColor: '#0071e3',
                      boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.12)',
                    },
                  }}
                />
              ))}
            </Stack>
            {error && <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>{error.message}</Typography>}
          </Box>
        );
      }}
    />
  );
};

// ----------------------------------------------------------------------

export default function RegisterForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loginMethod, setLoginMethod] = useState('otp'); // Default to OTP/Email flow
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(36);

  const { isLoading, isAuthenticated } = useSelector((state) => state.auth);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Must be a valid email').required('Email is required'),
    password: loginMethod === 'password' ? Yup.string().required('Password is required') : Yup.string(),
    otp:
      loginMethod === 'otp' && otpSent
        ? Yup.string().length(6, 'OTP must be 6 digits').required('OTP is required')
        : Yup.string(),
  });

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues: { email: '', password: '', otp: '', remember: true },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    watch,
  } = methods;

  const currentEmail = watch('email');
  const currentOtp = watch('otp');

  const onSubmit = async (data) => {
    if (loginMethod === 'password') {
      dispatch(loginUser(data));
    } else {
      if (!otpSent) {
        const success = await dispatch(sendOTP(data.email));
        if (success) {
          setOtpSent(true);
          setCountdown(36);
        }
      } else {
        dispatch(loginWithOTP({ email: data.email, otp: data.otp }));
      }
    }
  };

  const handleGoogleLogin = () => {
    if (window.google) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID',
        scope: 'email profile',
        callback: (response) => {
          if (response && response.access_token) {
            dispatch(loginWithGoogle(response.access_token));
          }
        },
      });
      client.requestAccessToken();
    } else {
      console.error('Google Identity Services script not loaded');
    }
  };

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    let timer;
    if (loginMethod === 'otp' && otpSent && countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [loginMethod, otpSent, countdown]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {loginMethod === 'otp' && otpSent ? (
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ color: '#6e6e73', fontSize: '0.9rem', mb: 0.5 }}>
              To complete your account set up, enter the code sent to:
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ color: '#1d1d1f', fontWeight: 600, fontSize: '0.9rem' }}>{currentEmail}</Typography>
              <IconButton size="small" onClick={() => setOtpSent(false)} sx={{ color: '#6e6e73' }}>
                <Iconify icon="eva:edit-2-outline" width={16} />
              </IconButton>
            </Stack>
          </Box>

          <OTPInput name="otp" />

          <Typography sx={{ textAlign: 'center', color: '#6e6e73', fontSize: '0.875rem' }}>
            {countdown > 0 ? (
              `Resend OTP in ${countdown} seconds`
            ) : (
              <Link
                component="button"
                variant="body2"
                onClick={async (e) => {
                  e.preventDefault();
                  const success = await dispatch(sendOTP(currentEmail));
                  if (success) setCountdown(36);
                }}
                sx={{ color: '#0071e3', textDecoration: 'none' }}
              >
                Resend OTP
              </Link>
            )}
          </Typography>

          <SubmitButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            disabled={!currentOtp || currentOtp.length !== 6}
            loading={isSubmitting || isLoading}
          >
            Create account
          </SubmitButton>

          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mt: 2 }}>
            <Typography sx={{ color: '#6e6e73', fontSize: '0.875rem' }}>Already have an account?</Typography>
            <Link
              component="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/login');
              }}
              sx={{
                color: '#0071e3',
                fontSize: '0.875rem',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in
            </Link>
          </Stack>
        </Stack>
      ) : (
        <>
          <Stack spacing={2.5}>
            <AppleTextField name="email" label="Email Address" disabled={otpSent} />

            {loginMethod === 'password' && (
              <AppleTextField
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Stack>

          <SubmitButton
            sx={{ marginTop: '16px' }}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting || isLoading}
          >
            {loginMethod === 'otp' && !otpSent ? 'Continue' : 'Sign up'}
          </SubmitButton>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              OR
            </Typography>
          </Divider>

          <SocialButton
            fullWidth
            variant="outlined"
            startIcon={<Iconify icon="logos:google-icon" />}
            onClick={handleGoogleLogin}
          >
            Continue with Google
          </SocialButton>

          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
            <Typography
              onClick={() => {
                navigate('/login');
              }}
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                textAlign: 'center',
              }}
            >
              Already have an account?
            </Typography>
            <Typography
              onClick={() => {
                navigate('/login');
              }}
              sx={{
                fontSize: '0.875rem',
                color: '#0071e3',
                cursor: 'pointer',
                textAlign: 'center',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in
            </Typography>
          </Stack>
        </>
      )}
    </FormProvider>
  );
}
